import {
    Box,
    Button,
    Dialog,
    Flex,
    Input,
    Portal,
    Spinner,
    Text,
} from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { buildDcfPayload } from './useDcfCalculation';
import { calculateExitMultipleValuation } from './exitMultipleDcf';

const SAVED_FIELDS = [
    'baseRevenue',
    'stage1Years',
    'stage1Growth',
    'stage2Years',
    'stage2Growth',
    'fcfGrowthRate',
    'startingFcf',
    'operatingMarginStart',
    'operatingMarginTarget',
    'taxRate',
    'dAndAPctRevenue',
    'capexPctRevenue',
    'nwcPctRevenueChange',
    'wacc',
    'terminalGrowth',
    'exitMultiple',
    'forecastYears',
    'netDebt',
    'totalDebt',
    'preferredStock',
    'noncontrollingInterest',
    'cash',
    'sharesOutstanding',
    'projections',
    'projectionsCustomized',
    'actuals',
    'actualsCustomized',
];

export function buildDcfScenarioAssumptions(state) {
    return Object.fromEntries(SAVED_FIELDS.map((field) => [field, state[field]]));
}

function apiMessage(error, fallback) {
    return error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;
}

function formatPercent(value) {
    return Number.isFinite(Number(value)) ? `${(Number(value) * 100).toFixed(1)}%` : '—';
}

function formatMoney(value) {
    return Number.isFinite(Number(value))
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value)
        : '—';
}

function formatLargeMoney(value) {
    if (!Number.isFinite(Number(value))) return '—';
    return `$${new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value)}`;
}

const COMPARISON_ROWS = [
    ['Stage 1 growth', ({ assumptions }) => formatPercent(assumptions.stage1Growth)],
    ['Stage 2 growth', ({ assumptions }) => formatPercent(assumptions.stage2Growth)],
    ['Operating margin start', ({ assumptions }) => formatPercent(assumptions.operatingMarginStart)],
    ['Operating margin target', ({ assumptions }) => formatPercent(assumptions.operatingMarginTarget)],
    ['WACC', ({ assumptions }) => formatPercent(assumptions.wacc)],
    ['Exit multiple', ({ assumptions }) => (
        Number.isFinite(Number(assumptions.exitMultiple))
            ? `${Number(assumptions.exitMultiple).toFixed(1)}x`
            : '—'
    )],
    ['Terminal growth', ({ assumptions }) => formatPercent(assumptions.terminalGrowth)],
    ['Enterprise value', ({ result }) => formatLargeMoney(result.enterprise_value)],
    ['Equity value', ({ result }) => formatLargeMoney(result.equity_value)],
    ['Value per share', ({ result }) => formatMoney(result.intrinsic_value_per_share)],
    ['Upside vs price', ({ result, currentPrice }) => (
        Number(currentPrice) > 0
            ? formatPercent((result.intrinsic_value_per_share / Number(currentPrice)) - 1)
            : '—'
    )],
];

export default function DcfScenarios({
    ticker,
    assumptions,
    dispatch,
    currentPrice,
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect,
    calculationLoading,
    calculationError,
}) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [saves, setSaves] = useState([]);
    const [name, setName] = useState('');
    const [saveOpen, setSaveOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [comparison, setComparison] = useState([]);
    const [comparing, setComparing] = useState(false);
    const [loadingSaveId, setLoadingSaveId] = useState(null);
    const [loadCalculationStarted, setLoadCalculationStarted] = useState(false);
    const [sharingSaveId, setSharingSaveId] = useState(null);
    const [sharedLoading, setSharedLoading] = useState(false);
    const [activeScenario, setActiveScenario] = useState(null);
    const [updating, setUpdating] = useState(false);
    const loadedSharedTokenRef = useRef(null);
    const loadedOwnedScenarioRef = useRef(null);
    const apiUrl = process.env.REACT_APP_API_URL;
    const sharedToken = searchParams.get('scenario');
    const ownedScenarioId = searchParams.get('savedScenario');

    const authHeaders = useCallback(async () => {
        const token = await getAccessTokenSilently({ detailedResponse: false });
        return { Authorization: `Bearer ${token}` };
    }, [getAccessTokenSilently]);

    const loadScenario = useCallback((saveId, scenarioAssumptions, ownedScenario = null) => {
        setLoadingSaveId(saveId);
        setLoadCalculationStarted(false);
        setActiveScenario(ownedScenario);
        setError('');
        dispatch({ type: 'load_scenario', assumptions: scenarioAssumptions });
        window.requestAnimationFrame(() => {
            document.getElementById('model')?.scrollIntoView?.({
                behavior: 'smooth',
                block: 'start',
            });
        });
    }, [dispatch]);

    useEffect(() => {
        if (loadingSaveId == null) return;
        if (calculationLoading) {
            setLoadCalculationStarted(true);
            return;
        }
        if (!loadCalculationStarted) return;

        if (calculationError) {
            toast.error(`Scenario loaded, but recalculation failed: ${calculationError}`);
        } else {
            toast.success('Scenario loaded and recalculated.');
        }
        setLoadingSaveId(null);
        setLoadCalculationStarted(false);
    }, [calculationError, calculationLoading, loadCalculationStarted, loadingSaveId]);

    useEffect(() => {
        if (!sharedToken || loadedSharedTokenRef.current === sharedToken) return;
        loadedSharedTokenRef.current = sharedToken;
        setSharedLoading(true);
        setError('');

        const loadSharedScenario = async () => {
            try {
                const { data } = await axios.get(
                    `${apiUrl}/api/dcf/shared/${encodeURIComponent(sharedToken)}`,
                );
                const sharedTicker = String(data?.ticker || '').toUpperCase();
                if (sharedTicker && sharedTicker !== ticker) {
                    navigate(
                        `/stock/${sharedTicker.toLowerCase()}/analysis?scenario=${encodeURIComponent(sharedToken)}`,
                        { replace: true },
                    );
                    return;
                }
                loadScenario(`shared:${sharedToken}`, data?.assumptions);
            } catch (requestError) {
                const message = apiMessage(requestError, 'Could not load the shared DCF scenario.');
                setError(message);
                toast.error(message);
            } finally {
                setSharedLoading(false);
            }
        };

        void loadSharedScenario();
    }, [apiUrl, loadScenario, navigate, sharedToken, ticker]);

    useEffect(() => {
        if (
            !ownedScenarioId
            || sharedToken
            || !isAuthenticated
            || loadedOwnedScenarioRef.current === ownedScenarioId
        ) return;
        if (!/^\d+$/.test(ownedScenarioId)) {
            setError('Invalid saved scenario URL.');
            return;
        }

        loadedOwnedScenarioRef.current = ownedScenarioId;
        setLoadingSaveId(Number(ownedScenarioId));
        setLoadCalculationStarted(false);
        setError('');

        const loadOwnedScenario = async () => {
            try {
                const headers = await authHeaders();
                const { data } = await axios.get(
                    `${apiUrl}/api/dcf/saves/${encodeURIComponent(ownedScenarioId)}`,
                    { headers },
                );
                const savedTicker = String(data?.ticker || '').toUpperCase();
                if (savedTicker && savedTicker !== ticker) {
                    navigate(
                        `/stock/${savedTicker.toLowerCase()}/analysis?savedScenario=${encodeURIComponent(ownedScenarioId)}`,
                        { replace: true },
                    );
                    return;
                }
                loadScenario(
                    data.id,
                    data.assumptions,
                    { id: data.id, name: data.name },
                );
            } catch (requestError) {
                const message = apiMessage(requestError, 'Could not load the saved DCF scenario.');
                setLoadingSaveId(null);
                setError(message);
                toast.error(message);
            }
        };

        void loadOwnedScenario();
    }, [
        apiUrl,
        authHeaders,
        isAuthenticated,
        loadScenario,
        navigate,
        ownedScenarioId,
        sharedToken,
        ticker,
    ]);

    const loadSaves = useCallback(async () => {
        if (!isAuthenticated || !ticker) return;
        setLoading(true);
        setError('');
        try {
            const headers = await authHeaders();
            const { data } = await axios.get(
                `${apiUrl}/api/dcf/saves?ticker=${encodeURIComponent(ticker)}`,
                { headers },
            );
            setSaves(Array.isArray(data) ? data : data?.saves || []);
        } catch (requestError) {
            setError(apiMessage(requestError, 'Could not load saved DCF scenarios.'));
        } finally {
            setLoading(false);
        }
    }, [apiUrl, authHeaders, isAuthenticated, ticker]);

    useEffect(() => {
        void loadSaves();
    }, [loadSaves]);

    const saveScenario = async () => {
        if (!name.trim()) {
            setError('Enter a scenario name.');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            const headers = await authHeaders();
            const { data } = await axios.post(`${apiUrl}/api/dcf/saves`, {
                ticker,
                name: name.trim(),
                assumptions: buildDcfScenarioAssumptions(assumptions),
            }, { headers });
            setSaves((current) => [data, ...current.filter((save) => save.id !== data.id)]);
            setActiveScenario({ id: data.id, name: data.name || name.trim() });
            loadedOwnedScenarioRef.current = String(data.id);
            navigate(
                `/stock/${ticker.toLowerCase()}/analysis?savedScenario=${encodeURIComponent(data.id)}`,
                { replace: true },
            );
            setName('');
            setSaveOpen(false);
            toast.success('Scenario saved as new.');
        } catch (requestError) {
            setError(apiMessage(requestError, 'Could not save DCF assumptions.'));
        } finally {
            setSubmitting(false);
        }
    };

    const updateScenario = async () => {
        if (!activeScenario) return;
        setUpdating(true);
        setError('');
        try {
            const headers = await authHeaders();
            const { data } = await axios.patch(
                `${apiUrl}/api/dcf/saves/${encodeURIComponent(activeScenario.id)}`,
                {
                    name: activeScenario.name,
                    assumptions: buildDcfScenarioAssumptions(assumptions),
                },
                { headers },
            );
            setSaves((current) => current.map((save) => (
                save.id === data.id ? data : save
            )));
            setActiveScenario({ id: data.id, name: data.name || activeScenario.name });
            toast.success(`Updated “${data.name || activeScenario.name}”.`);
        } catch (requestError) {
            const message = apiMessage(requestError, 'Could not update the DCF scenario.');
            setError(message);
            toast.error(message);
        } finally {
            setUpdating(false);
        }
    };

    const toggleComparison = (saveId) => {
        setSelectedIds((current) => {
            if (current.includes(saveId)) return current.filter((id) => id !== saveId);
            return current.length < 3 ? [...current, saveId] : current;
        });
        setComparison([]);
    };

    const openOwnedScenario = (save) => {
        const saveId = String(save.id);
        if (ownedScenarioId === saveId) {
            loadScenario(save.id, save.assumptions, { id: save.id, name: save.name });
            return;
        }
        setLoadingSaveId(save.id);
        setLoadCalculationStarted(false);
        setError('');
        navigate(`/stock/${ticker.toLowerCase()}/analysis?savedScenario=${encodeURIComponent(saveId)}`);
    };

    const copyShareLink = async (save) => {
        setSharingSaveId(save.id);
        setError('');
        try {
            const headers = await authHeaders();
            const { data } = await axios.post(
                `${apiUrl}/api/dcf/saves/${encodeURIComponent(save.id)}/share`,
                {},
                { headers },
            );
            const token = data?.share_token;
            if (!token) throw new Error('The server did not return a share token.');
            const sharedTicker = String(data?.ticker || ticker).toLowerCase();
            const url = `${window.location.origin}/stock/${sharedTicker}/analysis?scenario=${encodeURIComponent(token)}`;
            if (!navigator.clipboard?.writeText) {
                throw new Error('Clipboard access is unavailable in this browser.');
            }
            await navigator.clipboard.writeText(url);
            toast.success('Share link copied.');
        } catch (requestError) {
            const message = apiMessage(requestError, 'Could not create the share link.');
            setError(message);
            toast.error(message);
        } finally {
            setSharingSaveId(null);
        }
    };

    const compareScenarios = async () => {
        const selected = selectedIds
            .map((id) => saves.find((save) => save.id === id))
            .filter(Boolean);
        if (selected.length < 2) return;

        setComparing(true);
        setError('');
        setComparison([]);
        try {
            const results = await Promise.all(selected.map(async (save) => {
                if (Array.isArray(save.assumptions?.projections) && save.assumptions.projections.length > 0) {
                    const merged = { ...assumptions, ...save.assumptions };
                    const valuation = calculateExitMultipleValuation({
                        projections: merged.projections,
                        wacc: merged.wacc,
                        exitMultiple: merged.exitMultiple,
                        totalDebt: merged.totalDebt,
                        preferredStock: merged.preferredStock,
                        noncontrollingInterest: merged.noncontrollingInterest,
                        cash: merged.cash,
                        sharesOutstanding: merged.sharesOutstanding,
                    });
                    return {
                        ...save,
                        result: {
                            enterprise_value: valuation.enterpriseValue,
                            equity_value: valuation.equityValue,
                            intrinsic_value_per_share: valuation.sharePrice,
                        },
                    };
                }
                const { data } = await axios.post(
                    `${apiUrl}/api/dcf`,
                    buildDcfPayload({ ...assumptions, ...save.assumptions }),
                );
                return { ...save, result: data };
            }));
            setComparison(results);
        } catch (requestError) {
            setError(apiMessage(requestError, 'Could not compare DCF scenarios.'));
        } finally {
            setComparing(false);
        }
    };

    if (!isAuthenticated) {
        const returnTo = ownedScenarioId
            ? `/stock/${ticker.toLowerCase()}/analysis?savedScenario=${encodeURIComponent(ownedScenarioId)}`
            : `/stock/${ticker.toLowerCase()}/analysis`;
        return (
            <Flex direction="column" gap={3}>
                {sharedLoading && <Flex align="center" gap={2}><Spinner size="sm" color="green.500" /><Text>Loading shared scenario…</Text></Flex>}
                {error && <Text color="red.600">{error}</Text>}
                <Flex align="center" justify="space-between" gap={4} flexWrap="wrap">
                    <Text color="gray.600">
                        {ownedScenarioId ? 'Sign in to load this saved scenario.' : 'Sign in to save and load DCF scenarios.'}
                    </Text>
                    <Button colorPalette="green" onClick={() => loginWithRedirect({
                        appState: { returnTo },
                    })}>
                        Sign in to save scenarios
                    </Button>
                </Flex>
            </Flex>
        );
    }

    return (
        <Flex direction="column" gap={4}>
            <Flex gap={3} align="center" flexWrap="wrap">
                {activeScenario ? (
                    <>
                        <Button colorPalette="green" onClick={updateScenario} loading={updating} loadingText="Updating">
                            Update “{activeScenario.name || 'Untitled scenario'}”
                        </Button>
                        <Button variant="outline" onClick={() => setSaveOpen(true)}>Save as new</Button>
                        <Text fontSize="sm" color="gray.600">Editing {activeScenario.name || 'Untitled scenario'}</Text>
                    </>
                ) : (
                    <Button colorPalette="green" onClick={() => setSaveOpen(true)}>
                        {sharedToken ? 'Save as new' : 'Save Scenario'}
                    </Button>
                )}
                <Button variant="outline" onClick={loadSaves} loading={loading}>Refresh</Button>
            </Flex>
            {error && <Text color="red.600">{error}</Text>}
            {loading && <Spinner color="green.500" />}
            {!loading && saves.length === 0 && <Text color="gray.600">No saved scenarios for {ticker}.</Text>}
            {saves.map((save) => (
                <Flex key={save.id} borderWidth="1px" borderRadius="lg" p={3} align="center" justify="space-between">
                    <Flex align="center" gap={3}>
                        <input
                            type="checkbox"
                            aria-label={`Compare ${save.name || 'Untitled scenario'}`}
                            checked={selectedIds.includes(save.id)}
                            disabled={!selectedIds.includes(save.id) && selectedIds.length >= 3}
                            onChange={() => toggleComparison(save.id)}
                        />
                        <Box>
                            <Text fontWeight="semibold">{save.name || 'Untitled scenario'}</Text>
                            {save.updated_at && <Text fontSize="xs" color="gray.500">Updated {new Date(save.updated_at).toLocaleString()}</Text>}
                        </Box>
                    </Flex>
                    <Flex gap={2}>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyShareLink(save)}
                            loading={sharingSaveId === save.id}
                            loadingText="Copying"
                            disabled={loadingSaveId != null}
                        >
                            Copy link
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openOwnedScenario(save)}
                            loading={loadingSaveId === save.id}
                            loadingText="Loading"
                            disabled={loadingSaveId != null && loadingSaveId !== save.id}
                        >
                            Load
                        </Button>
                    </Flex>
                </Flex>
            ))}
            {saves.length >= 2 && (
                <Box>
                    <Button
                        variant="outline"
                        onClick={compareScenarios}
                        disabled={selectedIds.length < 2}
                        loading={comparing}
                    >
                        Compare selected ({selectedIds.length})
                    </Button>
                    <Text mt={2} fontSize="sm" color="gray.600">Select 2–3 scenarios.</Text>
                </Box>
            )}
            {comparison.length > 0 && (
                <Box overflowX="auto">
                    <Box as="table" width="full" minWidth="600px" borderCollapse="collapse" aria-label="Scenario comparison">
                        <Box as="thead">
                            <Box as="tr">
                                <Box as="th" p={2} textAlign="left">Metric</Box>
                                {comparison.map((scenario) => (
                                    <Box as="th" p={2} textAlign="right" key={scenario.id}>{scenario.name}</Box>
                                ))}
                            </Box>
                        </Box>
                        <Box as="tbody">
                            {COMPARISON_ROWS.map(([label, value]) => (
                                <Box as="tr" borderTopWidth="1px" key={label}>
                                    <Box as="th" scope="row" p={2} textAlign="left" fontWeight="medium">{label}</Box>
                                    {comparison.map((scenario) => (
                                        <Box as="td" p={2} textAlign="right" key={scenario.id}>
                                            {value({ ...scenario, currentPrice })}
                                        </Box>
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            )}

            <Dialog.Root open={saveOpen} onOpenChange={({ open }) => setSaveOpen(open)}>
                <Portal>
                    <Dialog.Backdrop />
                    <Dialog.Positioner>
                        <Dialog.Content>
                            <Dialog.Header><Dialog.Title>{activeScenario || sharedToken ? 'Save as New Scenario' : 'Save DCF Scenario'}</Dialog.Title></Dialog.Header>
                            <Dialog.Body>
                                <Input
                                    aria-label="Scenario name"
                                    value={name}
                                    onChange={(event) => setName(event.target.value)}
                                    maxLength={100}
                                    placeholder="e.g. Base case"
                                />
                            </Dialog.Body>
                            <Dialog.Footer>
                                <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
                                <Button colorPalette="green" onClick={saveScenario} loading={submitting}>
                                    {activeScenario || sharedToken ? 'Save as new' : 'Save'}
                                </Button>
                            </Dialog.Footer>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>
        </Flex>
    );
}
